import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Chip, TextField } from "@mui/material";

import CopyIconButton from "components/shared/CopyIconButton/CopyIconButton";
import { groupPermissionsByNamespace } from "utils/accessManagement/roleDisplay";

// Read-only view of a permission list grouped by resource namespace (platform /
// organization / user / other), collapsible per group with in-group search —
// used by the role detail drawer. RoleEditDrawer renders its own selectable
// variant since editing needs checkboxes rather than copy actions.
const PermissionGroupList = ({ permissions }) => {
  const [search, setSearch] = useState("");
  const groups = useMemo(() => groupPermissionsByNamespace(permissions), [permissions]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups
      .map((g) => ({ ...g, permissions: g.permissions.filter((p) => p.toLowerCase().includes(q)) }))
      .filter((g) => g.permissions.length > 0);
  }, [groups, search]);

  if (permissions.length === 0) {
    return <p style={{ fontSize: "13px", color: "#9ca3af" }}>This role has no permissions.</p>;
  }

  return (
    <div>
      <TextField
        size="small"
        fullWidth
        placeholder="Search permissions…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
      />
      {filteredGroups.length === 0 && <p style={{ fontSize: "13px", color: "#9ca3af" }}>No permissions match &quot;{search}&quot;.</p>}
      {filteredGroups.map((group) => (
        <Accordion key={group.namespace} defaultExpanded disableGutters sx={{ boxShadow: "none", border: "1px solid #e5e7eb", mb: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}>
            <span style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#374151" }}>
              {group.label}
            </span>
            <Chip label={group.permissions.length} size="small" sx={{ fontSize: "10px", height: 18 }} />
          </AccordionSummary>
          <AccordionDetails sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0 }}>
            {group.permissions.map((permission) => (
              <span
                key={permission}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  fontFamily: "monospace",
                  fontSize: "11px",
                  color: "#374151",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 4,
                  padding: "2px 4px 2px 8px",
                }}
              >
                {permission}
                <CopyIconButton value={permission} label="Copy permission" />
              </span>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

PermissionGroupList.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PermissionGroupList;
